import { Container } from '@synqly/ui/components/container'
import { LinkContext } from '@synqly/ui/components/link'
import { useEnv } from '@synqly/ui/context/env'
import { Layout as DefaultLayout } from '@synqly/ui/layouts/management/page-layout'
import { App, getInitialProps } from '@synqly/ui/pages/_app'
import {
  DashboardIcon,
  GearIcon,
  InfoCircledIcon,
  Link1Icon,
  ListBulletIcon,
  TargetIcon,
} from '@synqly/ui/radix/icons'
import { Flex, Switch, Text } from '@synqly/ui/radix/themes'
import { useRouter } from 'next/router'

import '@synqly/ui/styles/theme-management.css'
import styles from './management-app.module.css'
import { useAccountEnvironment } from '@/ui/hooks/use-account-env'
import * as Disclosure from '@synqly/ui/components/disclosure'
import AuthPage from './auth/[...management-auth]'
import { AccountEnvironment } from '@/util/is-valid-environment'

export { ManagementApp as default }

function ManagementApp(props) {
  const router = useRouter()
  const { environment } = useAccountEnvironment()
  const { Component } = props

  if (Component === AuthPage) {
    return <App {...props} />
  }

  if (router.asPath === '/') {
    try {
      router.replace({
        pathname: `/[env]/${router.asPath}`,
        query: {
          ...router.query,
          env: AccountEnvironment.PROD,
        },
      })
    } catch {
      /* empty */
    }
  }

  const metadata = {
    ...initialMetadata,
    other: {
      links: [
        ...initialMetadata.other.links,
        {
          rel: 'synqly:nav:primary:/[env]',
          title: <EnvironmentSwitch environment={environment} />,
        },
      ],
    },
  }

  return (
    <App
      defaultLayout={ManagementLayout}
      defaultMetadata={metadata}
      {...props}
    />
  )
}

function ManagementLayout(props) {
  const { environment } = useAccountEnvironment()
  const { NEXT_PUBLIC_APP_URL: origin } = useEnv()

  return (
    <LinkContext.Provider value={{ basePath: `/${environment}`, origin }}>
      <Flex direction="column">
        <Disclosure.Root open={environment === 'test'}>
          <Disclosure.Content>
            <Container className={styles.environmentBanner}>
              <Flex gap="4" justify="center">
                <Text weight="bold">Test mode</Text>
                <Text>You’re now working only with test accounts.</Text>
              </Flex>
            </Container>
          </Disclosure.Content>
        </Disclosure.Root>
        <DefaultLayout {...props} />
      </Flex>
    </LinkContext.Provider>
  )
}

function EnvironmentSwitch({ environment }) {
  const router = useRouter()

  return (
    <Flex className={styles.environmentSwitch}>
      <Text>Test mode</Text>
      <Switch
        defaultChecked={environment === 'test'}
        onCheckedChange={(test) =>
          router.push({
            pathname: router.pathname,
            query: {
              ...router.query,
              env: test ? 'test' : 'prod',
            },
          })
        }
      />
    </Flex>
  )
}

ManagementApp.getInitialProps = getInitialProps

/**
 * @typedef {Object} MetadataLink
 * @property {string | function} href
 * @property {string} rel
 * @property {string | JSX.Element} title
 */

/**
 * @typedef {Object} Metadata
 * @property {Object} other
 * @property {MetadataLink[]} other.links
 */

/** @type {Metadata} */
const initialMetadata = {
  other: {
    links: [
      {
        href: '/',
        rel: 'synqly:nav:primary:/dash',
        title: (
          <>
            <DashboardIcon />
            Dashboard
          </>
        ),
      },
      {
        href: '/issues',
        rel: 'synqly:nav:primary:/data',
        title: (
          <>
            <InfoCircledIcon />
            Issues
          </>
        ),
      },
      {
        href: '/accounts',
        rel: 'synqly:nav:primary:/data',
        title: (
          <>
            <ListBulletIcon />
            Accounts
          </>
        ),
      },
      {
        href: '/integration-points',
        rel: 'synqly:nav:primary:/data',
        title: (
          <>
            <TargetIcon />
            Integration Points
          </>
        ),
      },
      {
        href: '/integrations',
        rel: 'synqly:nav:primary:/data',
        title: (
          <>
            <Link1Icon />
            All Integrations
          </>
        ),
      },
      // TODO: Add global jobs page when the APIs are usable
      // {
      //   href: '/jobs',
      //   rel: 'synqly:nav:primary:/data',
      //   title: (
      //     <>
      //       <ClockIcon />
      //       Jobs
      //     </>
      //   ),
      // },
      {
        href: '/settings',
        rel: 'synqly:nav:primary:/settings',
        title: (
          <>
            <GearIcon />
            Settings
          </>
        ),
      },
      {
        href: 'https://docs.synqly.com/guides/getting-started/overview',
        rel: 'synqly:nav:primary:/docs',
        title: 'Getting started',
      },
      {
        href: 'https://docs.synqly.com/api-reference/overview',
        rel: 'synqly:nav:primary:/docs',
        title: 'API Reference',
      },
    ],
  },
}
